import type React from 'react'
import { type ForwardRefRenderFunction, forwardRef, useImperativeHandle, useRef } from 'react'

import {
  type AutocompleteRenderInputParams,
  InputAdornment,
  type InputProps as MUIInputProps,
} from '@mui/material'
import { Info, Label, TextButton } from 'components'

import {
  ArrowDownStyled,
  ArrowUpStyled,
  ErrorIconStyled,
  FormControlStyled,
  LabelAdornmentStyled,
  LabelRow,
  MaxLengthStyled,
  MoneyStyled,
  PercentStyled,
  SearchIconStyled,
  SpinButtonContainer,
  TextFieldStyled,
  WrapLabelStyled,
} from './styles'

export enum DSInputType {
  MASTER_INPUT = 'hav-master-field-input',
  TEXT_INPUT = 'hav-text-field-input',
  SEARCH_INPUT = 'hav-search-field-input',
  PERCENT_INPUT = 'hav-percent-field-input',
  CURRENCY_INPUT = 'hav-currency-field-input',
  NUMBER_INPUT = 'hav-number-field-input',
}

export interface InputProps {
  design?: DSInputType
  name?: string
  linkLabel?: string
  id?: string
  autocompleteParams?: AutocompleteRenderInputParams
  value: string | number
  label?: string
  info?: string
  disabled?: boolean
  type?: 'text' | 'number' | 'password' | 'email'
  InputProps?: MUIInputProps
  onClick?: React.MouseEventHandler<HTMLDivElement>
  addormentOnClick?: React.MouseEventHandler<SVGSVGElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  addorment?: boolean
  error?: string | null
  errorOutline?: boolean
  required?: boolean
  width?: number
  height?: number
  rows?: number
  autoSize?: boolean
  placeholder?: string
  textUppercase?: boolean
  browserAutocomplete?: 'on' | 'off'
  showMaxLength?: boolean
  maxLength?: number
  maxLenghtPosition?: 'top' | 'bottom'
  labelAdornment?: React.ReactNode
  helperText?: string
  successOutline?: boolean
  hasWarning?: boolean
}

// TODO: Refactor this component to reduce the number of props
const InputComponent: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  {
    design = DSInputType.MASTER_INPUT,
    autocompleteParams = null,
    name = undefined,
    linkLabel,
    disabled,
    id,
    value = '',
    label,
    info,
    type = 'text',
    InputProps,
    onBlur = () => {
      return
    },
    onFocus = () => {
      return
    },
    onClick = () => {
      return
    },
    addormentOnClick,
    onChange,
    onKeyDown,
    addorment = false,
    required = false,
    error = null,
    errorOutline = false,
    hasWarning = false,
    width,
    height = 30,
    rows = 1,
    placeholder,
    textUppercase = false,
    browserAutocomplete = 'on',
    maxLength,
    maxLenghtPosition = 'bottom',
    showMaxLength = false,
    labelAdornment,
    autoSize = false,
    helperText,
    successOutline,
  },
  ref,
) => {
  const hasError = () => {
    return error !== '' && error !== null
  }
  const isAutoSize = autoSize ? { minRows: rows } : { height, rows }

  const internalRef = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => internalRef.current as HTMLInputElement)

  const handleStepUp = () => {
    if (internalRef.current) {
      internalRef.current.focus()
      internalRef.current.stepUp()
      const newValue = internalRef.current.value
      if (onChange) {
        const event = {
          target: { name, value: newValue },
        } as React.ChangeEvent<HTMLInputElement>
        onChange(event)
      }
    }
  }

  const handleStepDown = () => {
    if (internalRef.current) {
      internalRef.current.focus()
      internalRef.current.stepDown()
      const newValue = internalRef.current.value
      if (onChange) {
        const event = {
          target: { name, value: newValue },
        } as React.ChangeEvent<HTMLInputElement>
        onChange(event)
      }
    }
  }

  const hasAddorment = () => {
    return addorment || hasError() || design === DSInputType.NUMBER_INPUT
  }

  const renderIcon = (dsInputType: DSInputType) =>
    hasAddorment() ? (
      <InputAdornment position="end">
        {dsInputType === DSInputType.SEARCH_INPUT ? (
          <SearchIconStyled
            data-testid="icon-search"
            onClick={addormentOnClick}
            style={addormentOnClick ? { cursor: 'pointer' } : { cursor: 'auto' }}
          />
        ) : hasError() ? (
          <ErrorIconStyled data-testid="icon-error" />
        ) : dsInputType === DSInputType.PERCENT_INPUT ? (
          <PercentStyled data-testid="icon-percent" />
        ) : dsInputType === DSInputType.CURRENCY_INPUT ? (
          <MoneyStyled data-testid="icon-money" />
        ) : design === DSInputType.NUMBER_INPUT ? (
          <SpinButtonContainer>
            <ArrowUpStyled onClick={handleStepUp} onMouseDown={(e) => e.preventDefault()} />
            <ArrowDownStyled onClick={handleStepDown} onMouseDown={(e) => e.preventDefault()} />
          </SpinButtonContainer>
        ) : null}
      </InputAdornment>
    ) : null
  return (
    <>
      {label && (
        <LabelRow>
          <WrapLabelStyled disabled={disabled} hasInfo={!!info}>
            <Label htmlFor={name} id="LabelInputForm" required={required}>
              {label}
            </Label>
            {!!info && <Info>{info}</Info>}
            {linkLabel && (
              <TextButton external url={linkLabel}>
                Ver Tabela
              </TextButton>
            )}
            {labelAdornment && <LabelAdornmentStyled>{labelAdornment}</LabelAdornmentStyled>}
          </WrapLabelStyled>
          {showMaxLength && maxLength && maxLenghtPosition === 'top' && (
            <MaxLengthStyled position={maxLenghtPosition}>
              {value?.toString()?.length}/{maxLength}
            </MaxLengthStyled>
          )}
        </LabelRow>
      )}
      <FormControlStyled disabled={disabled} fullWidth {...(width && { width })}>
        <TextFieldStyled
          design={design}
          disabled={disabled}
          error={hasError() || errorOutline}
          hasWarning={hasWarning}
          inputRef={internalRef}
          textuppercase={textUppercase}
          {...autocompleteParams}
          helperText={helperText}
          name={name}
          showspinbuttons={design === DSInputType.NUMBER_INPUT}
          successoutline={successOutline}
          variant="outlined"
          {...isAutoSize}
          addorment={hasAddorment() ? 1 : undefined}
          autoComplete={browserAutocomplete}
          hasmultiline={rows > 1 || autoSize ? 1 : undefined}
          InputLabelProps={{ shrink: false }}
          InputProps={{
            ...InputProps,
            id: id,
            ...{
              'data-testid': 'input-component',
              endAdornment:
                InputProps?.endAdornment === undefined
                  ? renderIcon(design)
                  : InputProps?.endAdornment,
            },
            inputProps: {
              maxLength,
            },
          }}
          id={name}
          multiline={rows > 1 || autoSize}
          onBlur={onBlur}
          onChange={onChange}
          onClick={onClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          type={type}
          value={value}
          {...(error && { error: true, helperText: error })}
        />
        {showMaxLength && maxLength && maxLenghtPosition === 'bottom' && (
          <MaxLengthStyled position={maxLenghtPosition}>
            {value?.toString()?.length}/{maxLength}
          </MaxLengthStyled>
        )}
      </FormControlStyled>
    </>
  )
}

export const Input = forwardRef(InputComponent)
